import moment, { Moment } from "moment-timezone";
import { composeStartTimeAndDate } from "../../components/sessions/helper";
import { isValidMoment } from "../../helpers/date_helpers";
import ApiDateParser from "../../helpers/parsers/apiDateParser";
import BaseModel from "./BaseModel";
import CandidateModel from "./CandidateModel";
import NoteModel from "./NoteModel";
import StudentModel from "./StudentModel";
import TutorModel from "./TutorModel";
import { TelawahMap } from "./TelawahModel";
import SessionRatingModel from "./SessionRatingModel";

export enum SessionStates {
  scheduled = "scheduled",
  coming = "coming",
  done = "done",
  completed = "completed",
  in_progress = "in_progress",
  absence = "absence",
  canceled = "canceled",
  terminated_early = "terminated_early",
  canceled_by_tutor = "canceled_by_tutor",
  canceled_by_student = "canceled_by_student",
  tutor_absence = "tutor_absence",
  student_absence = "student_absence",
  unknown = "unknown",
}

export type SessionServiceProvider = "jitsi" | "bbb";
export type SponsorshipBranding = {
  enabled: boolean;
  logoUrl: string;
  name?: string | null;
};
export const statuses = [
  { "id": "completed", "value": SessionStates.completed },
  { "id": "unknown", "value": SessionStates.unknown },
  { "id": "terminated_early", "value": SessionStates.terminated_early },
  { "id": "canceled_by_tutor", "value": SessionStates.canceled_by_tutor },
  { "id": "canceled_by_student", "value": SessionStates.canceled_by_student },
  { "id": "tutor_absence", "value": SessionStates.tutor_absence },
  { "id": "student_absence", "value": SessionStates.student_absence },
]

export default class SessionModel extends BaseModel {
  session_id?: string;
  session_link?: string;
  viewed?: boolean;
  private _slotId?: string | number;
  private scheduledRecording?: string | null;
  reportedStatus:
    | "unreported"
    | "completed"
    | "canceled"
    | "absence"
    | "disconnected"
    | "disconnected_by_system"
    | "terminated_early"
    | "tutor_absence"
    | "canceled_by_student"
    | "student_absence"
    | "canceled_by_tutor";
  status: SessionStates;
  date?: Moment | null;
  start_time?: Moment | null;
  cancellation?: string;
  end_time?: Moment | null;
  startPageNumber?: number | string | null;
  endPageNumber?: number | string | null;
  sessionNumber?: number;
  notes?: NoteModel[] | null;
  student?: StudentModel | CandidateModel | null;
  tutor?: TutorModel | null;
  latency?: number | string | null;
  lastQuranPageReached?: number | null;
  external_meeting_id: number;
  session_number?: number;
  zoomMeetingInfo: any;
  _recordingUrl: string | undefined;
  sessionTelawahName?: string | null;
  meetingToken?: string;
  scheduleSlotType?: string;
  ratings?: SessionRatingModel[];
  session_duration?: number | string;
  tutor_tardiness?: number | string;
  serviceProvider: SessionServiceProvider;
  meetingUrl: string;
  externalRecordingUrl?: string;
  sponsorshipBranding?: SponsorshipBranding | null;

  constructor(arg: any) {
    super(arg);
    this.session_id = arg?.session_id!;
    this.session_link = arg?.session_link;
    this._slotId = arg?.slot_id;
    this.date = ApiDateParser.parse(!!arg.date ? arg.date : arg.created_at);
    this.start_time = ApiDateParser.parse(arg.start_time);
    this.end_time = ApiDateParser.parse(arg.end_time);
    this.startPageNumber = arg.start_page_number;
    this.endPageNumber = arg.end_page_number;
    this.session_number = arg?.session_number;
    this.notes = NoteModel.fromApiResponse(arg.notes);
    this.latency = !!arg.tardiness ? (arg.tardiness / 60).toFixed(0) : 0;
    this.session_duration = (arg?.session_duration / 60).toFixed(0);
    this.sessionNumber = arg.session_number;
    this.external_meeting_id = arg.external_meeting_id;
    this.zoomMeetingInfo = arg.external_meeting_info;
    this.reportedStatus = arg.reported_status;
    this.lastQuranPageReached = arg.last_quran_page_reached;
    this.meetingToken = arg.meeting_token ?? "";
    this.viewed = arg?.viewed ?? false;
    if (arg?.ratings)
      this.ratings = SessionRatingModel.fromApiResponse(arg.ratings);
    this.status = arg?.status;
    this.tutor_tardiness = !!arg?.tutor_tardiness
      ? (arg?.tutor_tardiness / 60).toFixed(0)
      : 0;
    this.cancellation =
      typeof arg?.cancellation === "string"
        ? arg?.cancellation ?? ""
        : arg?.cancellation?.cancellation_reason ?? "";
    this.scheduleSlotType = arg.schedule_slot_type ?? "recitation";
    if (!!arg?.session_telawah) {
      this.sessionTelawahName = arg?.session_telawah.name;
    } else {
      this.sessionTelawahName = "N/A";
    }
    if (!!arg.student) this.student = new StudentModel(arg.student);
    if (!!arg.candidate) this.student = new CandidateModel(arg.candidate);
    if (!!arg.tutor) this.tutor = new TutorModel(arg.tutor);
    this._recordingUrl = arg.recording_url;
    this.externalRecordingUrl = arg.external_recording_url;
    this.scheduledRecording = arg.should_be_recorded;
    this.serviceProvider = arg.service_provider;
    this.meetingUrl = arg.meeting_url;
    this.sponsorshipBranding = !!arg?.sponsorship_branding
      ? {
          enabled: !!arg.sponsorship_branding.enabled,
          logoUrl: arg.sponsorship_branding.logo_url,
          name: arg.sponsorship_branding.name ?? null,
        }
      : null;
  }

  get sessionStudentEndPageNumber(): number | null {
    if (this.endPageNumber != null) {
      return parseInt(this.endPageNumber.toString());
    }
    return null;
  }
  get tutorRating(): SessionRatingModel | undefined {
    if (!this.ratings) return undefined;
    const rating = this.ratings!.filter((rate) => rate.isTutorRater).at(0);
    return rating;
  }

  get studentRating(): SessionRatingModel | undefined {
    if (!this.ratings) return undefined;
    const rating = this.ratings!.filter((rate) => rate.isStudentRater).at(0);
    return rating;
  }

  get slotId() {
    return this._slotId;
  }

  get recordingUrl(): string | undefined {
    if (this.serviceProvider === "jitsi") return this._recordingUrl;
    return this.externalRecordingUrl;
  }

  private __checkIsInProgress(): boolean {
    // We assume that sessions with no ending time are in progress
    return !isValidMoment(this.end_time!);
  }

  private __checkIsDone(): boolean {
    const now = moment();
    const _endTime = composeStartTimeAndDate(this.end_time!, this.date!);
    return !this.__checkIsInProgress() && !!_endTime?.isBefore(now);
  }

  private __checkIsComing(): boolean {
    /**
     * We consider student's session upcoming if we are 15 minutes before the session start time or 45 minutes after the session start time (in case of delay).
     *
     * We consider candidate's session upcoming if we are 15 minutes before the session start time or 15 minutes after the session start time (in case of delay).
     */
    const TIME_BEFORE_START = 15;
    const TIME_AFTER_START = this.isCandidateSession ? 15 : 45;
    const now = moment();
    const _startTime = composeStartTimeAndDate(this.start_time!, this.date!);
    return (
      !this.__checkIsDone() &&
      _startTime.diff(now, "minutes") < TIME_BEFORE_START &&
      now.diff(_startTime, "minutes") < TIME_AFTER_START
    );
  }

  get shouldSessionStartRecording() {
    return !!this.scheduledRecording;
  }

  get statusReported(): keyof typeof SessionStates {
    if (this.reportedStatus === "absence") return SessionStates.absence;
    if (this.reportedStatus === "completed") return SessionStates.done;
    if (this.reportedStatus === "canceled") return SessionStates.canceled;
    if (this.reportedStatus === "terminated_early")
      return SessionStates.terminated_early;
    if (this.reportedStatus === "canceled_by_tutor")
      return SessionStates.canceled_by_tutor;
    if (this.reportedStatus === "canceled_by_student")
      return SessionStates.canceled_by_student;
    if (this.reportedStatus === "tutor_absence")
      return SessionStates.tutor_absence;
    if (this.reportedStatus === "student_absence")
      return SessionStates.student_absence;
    if (this.__checkIsInProgress()) return SessionStates.in_progress;
    if (this.__checkIsDone()) return SessionStates.done;
    if (this.__checkIsComing()) return SessionStates.coming;
    return SessionStates.scheduled;
  }

  // @deprecated
  get isInProgress(): boolean {
    return this.statusReported === SessionStates.in_progress;
  }

  // @deprecated use isReportedDone
  get isDone(): boolean {
    return this.isReportedDone;
  }

  get isReportedDone(): boolean {
    return this.statusReported === SessionStates.done;
  }

  // @deprecated
  get isComing(): boolean {
    return this.statusReported === SessionStates.coming;
  }

  // @deprecated
  get isScheduled(): boolean {
    return this.statusReported === SessionStates.scheduled;
  }

  // @deprecated
  get isAbsence(): boolean {
    return this.statusReported === SessionStates.absence;
  }

  // @deprecated
  get isCanceled(): boolean {
    return this.status === SessionStates.canceled;
  }

  get isAbsenceBySystem(): boolean {
    return (
      this.status === SessionStates.absence ||
      this.status === SessionStates.tutor_absence ||
      this.status === SessionStates.student_absence
    );
  }

  get isCanceledOrAbsence(): boolean {
    return (
      this.status === SessionStates.canceled ||
      this.status === SessionStates.canceled_by_student ||
      this.status === SessionStates.canceled_by_tutor ||
      this.status === SessionStates.absence ||
      this.status === SessionStates.tutor_absence ||
      this.status === SessionStates.student_absence
    );
  }

  get isClickableBySupervisor(): boolean {
    return (
      this.status === "completed" ||
      !!this.cancellation ||
      this.status === "terminated_early"
    );
  }
  get isViewableBySupervisor(): boolean {
    return this.status === "completed" || this.status === "terminated_early";
  }

  get isReportedCancelledOrAbsence(): boolean {
    return (
      this.reportedStatus === "canceled_by_student" ||
      this.reportedStatus === "canceled_by_tutor" ||
      this.reportedStatus === "canceled" ||
      this.reportedStatus === "student_absence" ||
      this.reportedStatus === "tutor_absence"
    );
  }

  get isLearnerSession(): boolean {
    return !!this.student!.isLearner;
  }

  setTelawahNameFromMapByStudent(map: TelawahMap) {
    this.sessionTelawahName = map[this.student!.telawah_id!];
  }

  get withinPermittedStartTimeWindow(): boolean {
    /**
     * We give ability to the tutor to start a student session only 5 minutes before the session start time and 45 minutes after the session start time
     * So if the session is scheduled for 10:00 AM, the tutor can start the session between 9:55 AM and 10:45 AM
     *
     * We also give ability to the tutor to start a candidate session only 2 minutes before the session start time and 15 minutes after the session start time
     * So if the session is scheduled for 10:00 AM, the tutor can start the session between 9:58 AM and 10:15 AM
     *
     * At the moment, student's sessions are scheduled for 1 hour and candidate's sessions are scheduled for 20 minutes
     */

    const TIME_BEFORE_START = 5;
    const TIME_AFTER_START =
      this.isCandidateSession || this.isLearnerSession ? 15 : 45;
    const now = moment();
    const _startTime = this.start_time!.clone();

    return (
      _startTime.diff(now, "minutes") < TIME_BEFORE_START &&
      now.diff(_startTime, "minutes") < TIME_AFTER_START
    );
  }

  get isPastPermittedStartTimeWindow(): boolean {
    const now = moment();
    const _endTime = this.end_time!.clone();
    const result = now.isAfter(_endTime);
    return result;
  }

  get isStartable(): boolean {
    return (
      (this.withinPermittedStartTimeWindow && !this.isReportedDone) ||
      this.isInProgress
    );
  }

  get canBeCanceled(): boolean {
    /**
     * We give the ability to the candidate to cancel a session only if we are more than 60 minutes(1 hour) before the session start time
     */
    const TIME_BEFORE_START = 60;
    const now = moment();
    const startTime = composeStartTimeAndDate(this.start_time!, this.date!);

    return (
      this.isScheduled && startTime.diff(now, "minutes") > TIME_BEFORE_START
    );
  }
  get isCandidateSession(): boolean {
    return this.student instanceof CandidateModel;
  }
}
